import SEO from "@/components/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const logoUrl = "https://storage.readdy-site.link/project_files/39ea62fd-ecaa-4170-8d69-37ba8843adfb/5c10aa27-d2a9-4ff1-8ad2-de2b3991206a_LogoGP.png?v=cf62be58e76af8f2a4ae26c38a0a823d";

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "Sobre | GodoyProd",
      url: `${siteUrl}/sobre`,
      description: "Conheça Matheus Godoy, produtor musical e especialista em transcrição de partituras gospel com mais de 15 anos de experiência.",
      inLanguage: "pt-BR",
      mainEntity: {
        "@type": "Person",
        name: "Matheus Godoy",
        jobTitle: "Produtor Musical e Transcritor de Partituras",
        url: `${siteUrl}/sobre`,
        image: "https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/5223407bf8eba585c8e17c573dd050eb.jpeg",
        sameAs: [
          "https://www.instagram.com/eugoodoy",
          "https://www.youtube.com/@GodoyProd",
          "https://open.spotify.com/user/eugoodoy?si=8d3a4aea30714493",
        ],
        worksFor: {
          "@type": "Organization",
          name: "GodoyProd",
          url: siteUrl,
          logo: logoUrl,
        },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "GodoyProd",
      url: siteUrl,
      logo: logoUrl,
      description: "Transcrição profissional de partituras gospel. Biblioteca digital, cursos de musicalização e comunidade de músicos.",
      sameAs: [
        "https://www.instagram.com/eugoodoy",
        "https://www.youtube.com/@GodoyProd",
        "https://open.spotify.com/user/eugoodoy?si=8d3a4aea30714493",
      ],
    },
  ];

  return (
    <>
      <SEO
        title="Sobre"
        description="Conheça Matheus Godoy, produtor musical e especialista em transcrição de partituras gospel com mais de 15 anos de experiência e 700 milhões de visualizações."
        keywords="Matheus Godoy, GodoyProd, produtor musical, transcrição partituras, partituras gospel, música gospel, Sibelius"
        ogImage="https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/5223407bf8eba585c8e17c573dd050eb.jpeg"
        schema={schema}
        canonicalPath="/sobre"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Professional%20music%20studio%20control%20room%20dark%20elegant%20golden%20accent%20lights%20recording%20equipment%20luxury%20atmosphere&width=1920&height=600&seq=godoy-about-hero&orientation=landscape')] bg-cover bg-center" />
          <div className="absolute inset-0 bg-godoy-black/80" />
          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto text-center">
            <span className="text-godoy-gold text-sm font-semibold tracking-widest uppercase">
              Sobre
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-godoy-cream mt-4">
              Matheus Godoy
            </h1>
            <p className="text-godoy-lightgray text-lg mt-4 max-w-2xl mx-auto">
              Produtor Musical & Especialista em Transcrição de Partituras
            </p>
          </div>
        </section>

        {/* Bio */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
              <div className="lg:col-span-2">
                <div className="aspect-[3/4] rounded-lg overflow-hidden border border-godoy-gray/20">
                  <img
                    src="https://static.readdy.ai/image/a7593ab01aa731c0cb39690e041d41d7/5223407bf8eba585c8e17c573dd050eb.jpeg"
                    alt="Matheus Godoy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex justify-center gap-3 mt-6">
                  <a
                    href="https://open.spotify.com/user/eugoodoy?si=8d3a4aea30714493"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-godoy-gray hover:bg-godoy-gold/20 text-godoy-cream hover:text-godoy-gold transition-all"
                  >
                    <i className="ri-spotify-fill text-lg" />
                  </a>
                  <a
                    href="https://www.instagram.com/eugoodoy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-godoy-gray hover:bg-godoy-gold/20 text-godoy-cream hover:text-godoy-gold transition-all"
                  >
                    <i className="ri-instagram-fill text-lg" />
                  </a>
                  <a
                    href="https://www.youtube.com/@GodoyProd"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-godoy-gray hover:bg-godoy-gold/20 text-godoy-cream hover:text-godoy-gold transition-all"
                  >
                    <i className="ri-youtube-fill text-lg" />
                  </a>
                </div>
              </div>

              <div className="lg:col-span-3 space-y-6">
                <div>
                  <h2 className="font-serif text-2xl md:text-3xl font-bold text-godoy-cream mb-4">
                    Quem é Matheus Godoy
                  </h2>
                  <p className="text-godoy-lightgray leading-relaxed">
                    Matheus Godoy é um produtor musical e especialista em transcrição de partituras,
                    com uma trajetória consolidada de mais de 15 anos no cenário gospel brasileiro.
                    Aos 30 anos, acumula uma experiência vasta que percorre desde a produção técnica
                    em estúdio até a execução em palcos nacionais e internacionais ao lado de alguns
                    dos maiores nomes da música cristã contemporânea.
                  </p>
                </div>

                <div>
                  <h3 className="font-serif text-xl font-semibold text-godoy-gold mb-3">
                    Trajetória Profissional
                  </h3>
                  <p className="text-godoy-lightgray leading-relaxed">
                    Ao longo de sua carreira, Matheus desenvolveu um portfólio expressivo na produção
                    audiovisual, tendo participado da gravação de diversos clipes oficiais de
                    artistas consagrados da gravadora Todah Music. Sua atuação abrange desde a
                    captação de áudio em estúdio até a produção técnica de conteúdo digital,
                    consolidando-o como um profissional versátil e completo no mercado musical gospel.
                  </p>
                </div>

                <div>
                  <h3 className="font-serif text-xl font-semibold text-godoy-gold mb-3">
                    Vivência em Turnês e Palco
                  </h3>
                  <p className="text-godoy-lightgray leading-relaxed">
                    A experiência de Matheus vai além do estúdio. Ele já viajou acompanhando
                    artistas de renome em turnês nacionais e internacionais, acumulando
                    experiência prática em produção ao vivo, sonorização e direção musical.
                    Essa vivência em estrada o torna sensível às necessidades reais de músicos
                    e ministérios de louvor, entendendo na prática o que funciona em um contexto
                    de adoração.
                  </p>
                </div>

                <div>
                  <h3 className="font-serif text-xl font-semibold text-godoy-gold mb-3">
                    Alcance e Impacto
                  </h3>
                  <p className="text-godoy-lightgray leading-relaxed">
                    Os números refletem a relevância do trabalho desenvolvido por Matheus: mais
                    de 200 milhões de visualizações acumuladas em clipes nos quais participou
                    pessoalmente como produtor e músico, e mais de 500 milhões de visualizações
                    participando como produtor técnico com captações e edições.
                  </p>
                </div>

                <div>
                  <h3 className="font-serif text-xl font-semibold text-godoy-gold mb-3 tracking-[0.1em] uppercase">
                    GodoyProd
                  </h3>
                  <p className="text-godoy-lightgray leading-relaxed">
                    Com a GodoyProd, Matheus canaliza toda essa experiência em um propósito
                    claro: democratizar o acesso a partituras de qualidade no segmento gospel.
                    Utilizando o Sibelius, software de referência mundial em notação musical,
                    ele oferece transcrições precisas e profissionais, além de uma biblioteca
                    em constante crescimento com as principais canções do gospel brasileiro.
                  </p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 pt-6 border-t border-godoy-gray/30">
                  <div className="text-center">
                    <div className="font-serif text-3xl font-bold text-godoy-gold">15+</div>
                    <div className="text-godoy-lightgray text-sm mt-1">Anos de Experiência</div>
                  </div>
                  <div className="text-center">
                    <div className="font-serif text-3xl font-bold text-godoy-gold">700M+</div>
                    <div className="text-godoy-lightgray text-sm mt-1">Visualizações</div>
                  </div>
                  <div className="text-center">
                    <div className="font-serif text-3xl font-bold text-godoy-gold">2500+</div>
                    <div className="text-godoy-lightgray text-sm mt-1">Partituras</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}